import { useTheme } from '@emotion/react'
import { WhiteBox, WhiteBoxHeader } from 'components/AppShell'
import { makeStyles } from '@mui/styles'
import React from 'react'
import { SearchTextField } from 'components/CustomFields'
import { Box, Divider, Skeleton, Typography } from '@mui/material'
import { FilterChips } from 'components/Filter'
import { useState } from 'react'
import HorizontalScrollNavigator from 'components/HorizontalScrollNavigator'
import { NoResultComponent } from 'components/Home/TodoList'
import CustomButton from 'components/CustomButton'
import Chip from 'components/Chip'
import ShortlistingTask from './ShortlistingTask'
import { useEffect } from 'react'
import { getShortlistingTask } from 'services/shortlistingService'
import { useServices, useSnackbar } from 'contexts'
import { Session } from 'utils'
import { getUserEnrollments } from 'services'
import dayjs from 'dayjs'
import { useNavigateWithClient } from 'hooks'
import NoAccessTooltip from 'components/NoAccessTooltip'
import { useSelectedBatchRemoved, useIsBatchRemoved } from 'announcementStore'

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    position: "relative",
    padding: theme.spacing(5),
    display: "flex",
    flexDirection: "column",
    justifyContent: 'space-between',
    width: "404px",
    height: "180px",
    border: `3px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius: theme.spacing(2),
    cursor: "pointer",
    flex: "0 0 auto",
    transition: "box-shadow 10ms linear",
    '&:hover': {
      border: `3px solid ${theme.palette.primary['clr-100']}`,
      transition: 'box-shadow 0.1s ease-in-out',
    },
    overflow: 'hidden',
  },
  cardContent: {
    display: "flex", 
    flexDirection: "column", 
    justifyContent: "flex-end", 
    zIndex: '2', 
    gap: theme.spacing(1)
  },
  interviewIcon: {
    position: 'absolute',
    top: '0px',
    right: '0px',
  },
}))

const filters = [
  { field: 'expiresSoon', type: 'bool', label: 'Expires Soon' },
  { field: 'new', type: 'bool', label: 'New Published' },
];

const TaskCardSkeleton = () => {
  const theme = useTheme();
  const classes = useStyles();

  return (
    <Box className={classes.cardContainer}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
        }}
      >
        {/* <Skeleton variant="circular" width={180} height={180} /> */}
      </Box>

      <Box display="flex" gap="8px" flexWrap="wrap">
        <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: '4px' }} />
        <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: '4px' }} />
      </Box>

      <Box display="flex" flexDirection="column" gap="8px" mt="auto">
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="90%" height={28} />
        <Box display="flex" gap="8px">
          <Skeleton variant="text" width="40%" height={20} />
          <Skeleton variant="text" width="50%" height={20} />
        </Box>
      </Box>
    </Box>
  );
};

const TaskCard = ({ task, onClick, key }) => {
  const classes = useStyles();
  const theme = useTheme();

  const isBatchRemoved = useIsBatchRemoved();
  const noAccess = isBatchRemoved(task.batch?._id || task.batch);

  const isNewTask = dayjs().diff(dayjs(task.createdAt), 'hour') <= 24;

  const expiresYear = dayjs(task.expiresOn).year();
  const isNeverExpires = expiresYear === 9999;
  const now = dayjs();
  const expiresOn = dayjs(task.expiresOn);
  const isExpired = !isNeverExpires && expiresOn.isBefore(now);
  const hoursLeft = expiresOn.diff(now, 'hour');
  const minutesLeft = expiresOn.diff(now, 'minute') % 60;
  const secondsLeft = expiresOn.diff(now, 'second') % 60;
  const lessThan24h = !isNeverExpires && !isExpired && expiresOn.diff(now, 'hour') < 24 && expiresOn.diff(now, 'second') > 0;

  let expirationText = '';
  let expirationColor = theme.palette.primary.main;
  let expirationBg = theme.palette.primary['clr-50'];

  if (isNeverExpires) {
    expirationText = 'Never Expires';
  } else if (isExpired) {
    expirationText = 'Expired';
    expirationColor = theme.palette.error.main;
    expirationBg = theme.palette.error['clr-50'] || '#FFEBEE';
  } else if (lessThan24h) {
    const pad = (n) => n.toString().padStart(2, '0');
    expirationText = `Expiring in: ${pad(hoursLeft)}:${pad(minutesLeft)}:${pad(secondsLeft)}`;
    expirationColor = theme.palette.error.main;
    expirationBg = theme.palette.error['clr-50'] || '#FFEBEE';
  } else {
    expirationText = `Expires on: ${expiresOn.format('DD/MM/YYYY')}`;
  }

  // Timer for live countdown if less than 24h
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (lessThan24h && !isExpired) {
      const interval = setInterval(() => setTick(t => t + 1), 1000);
      return () => clearInterval(interval);
    }
  }, [lessThan24h, isExpired]);

  const handleClick = () => {
    if (noAccess) return;
    if (isExpired) {
      if (typeof window !== 'undefined' && window.snackbar) {
        window.snackbar.error(' Submission is not allowed for the expired tasks');
      }
      return;
    }
    onClick(task);
  };

  return (
    <NoAccessTooltip show={noAccess}>
    <Box key={key} className={classes.cardContainer} onClick={handleClick} style={(isExpired || noAccess) ? { cursor: 'not-allowed', opacity: 0.7 } : {}}>
      <Box className={classes.interviewIcon}>
        <img
          width={180}
          height={180}
          src='https://assets.languify.in/images/circularPads.svg'
          alt="crc"
        />
      </Box>
      
      <Box display="flex" gap='8px' flexWrap='wrap'>
        <>
          {isNewTask && (
            <Chip
              content={"New"}
              color={theme.palette.tertiary['clr-700']}
              bgColor={theme.palette.tertiary['clr-100']}
              sx={{ padding: theme.spacing(1, 2), borderRadius: '4px' }}
            />
          )}
          <Chip
            content={expirationText}
            color={expirationColor}
            bgColor={expirationBg}
            sx={{ padding: theme.spacing(1, 2), borderRadius: '4px' }}
          />
        </>
      </Box>
      
      <Box className={classes.cardContent}>
        <Typography variant='body02-bold' color='neutral.clr-600'>
          {task.company ? 'Shortlisting task . Company specific' : 'Shortlisting task . Job role specific'}
        </Typography>
        <Typography variant='h4-semiBold'>
          {task?.name}
        </Typography>
        <Box display='flex' gap='4px'>
          {task.submitted && (
            <Typography variant='body02-bold' color='success.clr-700'>
              Submitted .
            </Typography>
          )}
          {task.score && (
            <Typography variant='body02-bold' color='primary.main'>
              Last Relevance Score: {task.score}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
    </NoAccessTooltip>
  )
}

const TodoList = ({ tasks, onTaskClick, loading }) => {
  return (
    <>
      {loading ?
        Array.from(new Array(3)).map((d, i) => {
          return <TaskCardSkeleton key={i} />
        })
        :
        tasks.map((task, index) => (
          <TaskCard
            task={task}
            key={index}
            onClick={onTaskClick}
          />
        ))
      }
    </>
  )
}

const ShortlistTodo = ({ refresh, setRefresh }) => {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const service = useServices();
  const navigateWithClient = useNavigateWithClient();
  const removed = useSelectedBatchRemoved();

  const [appliedFilters, setAppliedFilters] = useState({});
  const [shortlistingTasks, setShortlistingTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchBatchesAndTasks = async () => {
      try {
        setLoading(true);

        const { batchUserMappings } = await getUserEnrollments();
        const batchIds = batchUserMappings.map((bum) => bum.batch._id);

        const taskFilters = {
          user: Session.user.client._id,
          batch: batchIds,
          order: "desc",
          orderBy: "createdAt"
        };
        
        const personalizedTemplates = await service.callService(getShortlistingTask, taskFilters);
        setShortlistingTasks(personalizedTemplates.shortlistTasks);
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBatchesAndTasks();
  }, [refresh]);

  const isExpiringSoon = (task) => {
    const expiresYear = dayjs(task.expiresOn).year();
    if (expiresYear === 9999) return false; // Never expires
    
    const daysUntilExpiration = dayjs(task.expiresOn).diff(dayjs(), 'day');
    return daysUntilExpiration <= 7 && daysUntilExpiration >= 0;
  };

  const isNewTask = (task) => {
    return dayjs().diff(dayjs(task.createdAt), 'hour') <= 24;
  };

  const getFilteredTasks = () => {
    let filtered = [...shortlistingTasks];

    if (searchQuery.trim()) {
      filtered = filtered.filter(task =>
        task.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.company?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (appliedFilters.expiresSoon) {
      filtered = filtered.filter(task => isExpiringSoon(task));
    }

    if (appliedFilters.new) {
      filtered = filtered.filter(task => isNewTask(task));
    }

    return filtered;
  };

  const filteredTasks = getFilteredTasks();

  const handleFilterChange = ({ field, value }) => {
    setAppliedFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      if (value && !newFilters[field]) {
        newFilters[field] = value;
      } else {
        delete newFilters[field];
      }
      return newFilters;
    });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Patch window.snackbar for TaskCard
  React.useEffect(() => {
    window.snackbar = snackbar;
    return () => { window.snackbar = undefined; };
  }, [snackbar]);

  return (
    <WhiteBox sx={{ position: 'relative', marginTop: theme.spacing(4) }}>
      <WhiteBoxHeader
        id='to-do-list'
        heading="Shortlisting Tasks"
        sx={{ marginBottom: theme.spacing(4) }}
        subheading='Upload your resume in the shortlisting task & submit relevance score'
      >
        <SearchTextField
          placeholder="Find your task"
          value={searchQuery}
          onChange={handleSearchChange}
          disabled={!shortlistingTasks?.length}
        />
      </WhiteBoxHeader>

      <Divider />

      {shortlistingTasks.length > 0 && (
        <FilterChips
          title="Filter by"
          filters={filters}
          appliedFilters={appliedFilters}
          onChange={handleFilterChange}
          disabled={!shortlistingTasks.length}
        />
      )}

      <Box height={300}>
        {loading ? (
          <HorizontalScrollNavigator childrensLength={3}>
            <TodoList
              tasks={[]}
              onTaskClick={() => {}}
              loading={loading}
            />
          </HorizontalScrollNavigator>
        ) : filteredTasks.length ? (
          <Box display='flex' flexDirection='column' gap='16px'>
            <HorizontalScrollNavigator
              childrensLength={filteredTasks.length}
            >
              <TodoList
                tasks={filteredTasks}
                onTaskClick={(task) => {
                  if (task.submitted) {
                    snackbar.error('You can only submit it once')
                  } else {
                    setSelectedTask(task);
                    setOpenDialog(true);
                  }
                }}
                loading={loading}
              />
            </HorizontalScrollNavigator>
            <Typography variant='body01-semiBold' sx={{marginTop : '16px'}}>
              *You can check the relevance score multiple times. If not submitted before the task expires, the last score will be auto-submitted.
            </Typography>
          </Box>
        ) : (
          <NoResultComponent
            description={
              shortlistingTasks.length === 0 ? (
                <>No task has been published yet!<br />Curious to check how well your resume matches any job description?</>
              ) : (
                <>No tasks match your search criteria.<br />Try adjusting your filters or search terms.</>
              )
            }
            children={
              shortlistingTasks.length === 0 && (
                <NoAccessTooltip show={removed}>
                  <CustomButton
                    sx={{ marginTop: '28px' }}
                    onClick={() => {
                      navigateWithClient('/shortlisting-genie/jd-plus-resume')
                    }}
                    disabled={removed}
                  >
                    Check JD & Resume Relevance
                  </CustomButton>
                </NoAccessTooltip>
              )
            }
          />
        )}
      </Box>
      
      <ShortlistingTask
        openDialog={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedTask(null); 
          setRefresh(true);
        }}
        task={selectedTask}
      />
    </WhiteBox>
  );
};

export default ShortlistTodo